import React, { useEffect, useState } from "react";
import { Table, Spinner, Badge } from "react-bootstrap";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";

const API = process.env.REACT_APP_API;

const HEALTH_FIELDS = [
  { key: "has_everSyndrome", label: "โรคที่เคยเป็น" },
  { key: "has_drug", label: "แพ้ยา" },
  { key: "has_food", label: "แพ้อาหาร" },
  { key: "has_syndrome", label: "โรคประจำตัว" },
  { key: "has_facingSyndrome", label: "วิธีแก้ไข" },
  { key: "has_accidentHistory", label: "อุบัติเหตุ/อื่นๆ" },
];

const INFO_FIELDS = [
  { key: "has_idcard", label: "เลขบัตรประชาชน" },
  { key: "has_birthdate", label: "วันเกิด" },
  { key: "has_gender", label: "เพศ" },
  { key: "has_address", label: "ที่อยู่ปัจจุบัน" },
  { key: "has_contact", label: "ผู้ปกครองติดต่อได้" },
];

const YesNo = ({ v }) =>
  v ? (
    <i className="ri-checkbox-circle-fill text-success"></i>
  ) : (
    <i className="ri-close-circle-line text-danger"></i>
  );

// อ่านอย่างเดียว — โหลดรายละเอียดของงานหนึ่งเมื่อ expand แถวห้อง
export default function AdvisorTaskDetail({ duty, rId, yearId, semester, month }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (semester) params.set("semester", semester);
    if (month) params.set("month", month);
    axiosInstance
      .get(`${API}/getAdvisorTaskDetail/${duty}/${rId}/${yearId}?${params.toString()}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => { if (active) setRows(res.data || []); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [duty, rId, yearId, semester, month]);

  if (loading) {
    return (
      <div className="text-center text-muted py-3">
        <Spinner animation="border" size="sm" /> กำลังโหลด...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        <i className="ri-inbox-line fs-4 d-block mb-1"></i>
        {duty === "guidance" ? "ยังไม่มีการเช็คชื่อคาบแนะแนว" : "ไม่มีข้อมูล"}
      </div>
    );
  }

  // แนะแนว — รายครั้งที่เช็ค
  if (duty === "guidance") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 110 }}>วันที่</th>
            <th>หัวข้อ</th>
            <th>ผู้เช็ค</th>
            <th className="text-center" style={{ width: 60 }}>มา</th>
            <th className="text-center" style={{ width: 60 }}>ขาด</th>
            <th className="text-center" style={{ width: 60 }}>สาย</th>
            <th className="text-center" style={{ width: 60 }}>ลา</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.gtr_id}>
              <td>{r.att_date}</td>
              <td className="fs-sm">{r.gtr_topic}</td>
              <td className="fs-sm">{r.checked_by_name || <span className="text-muted">-</span>}</td>
              <td className="text-center">{Number(r.presents || 0)}</td>
              <td className="text-center">{Number(r.absents || 0)}</td>
              <td className="text-center">{Number(r.lates || 0)}</td>
              <td className="text-center">{Number(r.leaves || 0)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  // น้ำหนัก/ส่วนสูง — รายคน
  if (duty === "bodymass") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 90 }}>เลขประจำตัว</th>
            <th>ชื่อ-สกุล</th>
            <th className="text-center" style={{ width: 100 }}>ส่วนสูง (ซม.)</th>
            <th className="text-center" style={{ width: 100 }}>น้ำหนัก (กก.)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const done = r.stbm_height != null || r.stbm_weight != null;
            return (
              <tr key={i} className={done ? "" : "text-muted"}>
                <td>{r.st_idnumber}</td>
                <td className="fs-sm">{r.st_name}</td>
                <td className="text-center">{r.stbm_height != null ? r.stbm_height : "—"}</td>
                <td className="text-center">{r.stbm_weight != null ? r.stbm_weight : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  // คุณลักษณะอันพึงประสงค์ — รายคน
  if (duty === "desirable") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 90 }}>เลขประจำตัว</th>
            <th>ชื่อ-สกุล</th>
            <th className="text-center" style={{ width: 120 }}>กรอกแล้ว (ข้อ)</th>
            <th className="text-center" style={{ width: 100 }}>สถานะ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const filled = Number(r.filled_items || 0);
            return (
              <tr key={i}>
                <td>{r.st_idnumber}</td>
                <td className="fs-sm">{r.st_name}</td>
                <td className="text-center">{filled}/8</td>
                <td className="text-center">
                  {filled >= 8 ? (
                    <Badge bg="success">ครบ</Badge>
                  ) : filled > 0 ? (
                    <Badge bg="warning">ไม่ครบ</Badge>
                  ) : (
                    <Badge bg="danger">ยังไม่ทำ</Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  // ความเห็นครูที่ปรึกษา — รายคน
  if (duty === "opinion") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 90 }}>เลขประจำตัว</th>
            <th>ชื่อ-สกุล</th>
            <th className="text-center" style={{ width: 120 }}>บันทึกเมื่อ</th>
            <th className="text-center" style={{ width: 100 }}>สถานะ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.st_idnumber}</td>
              <td className="fs-sm">{r.st_name}</td>
              <td className="text-center">{r.updated_at || <span className="text-muted">—</span>}</td>
              <td className="text-center">
                {Number(r.recorded) ? <Badge bg="success">บันทึกแล้ว</Badge> : <Badge bg="danger">ยังไม่ทำ</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  // สุขภาพ / ข้อมูลนักเรียน — ตารางฟิลด์ ✓/✗ (ไม่แสดงค่าจริง)
  const fields = duty === "health" ? HEALTH_FIELDS : INFO_FIELDS;
  return (
    <Table size="sm" hover responsive className="mb-0 align-middle">
      <thead className="table-secondary">
        <tr>
          <th style={{ width: 90 }}>เลขประจำตัว</th>
          <th>ชื่อ-สกุล</th>
          {fields.map((f) => (
            <th key={f.key} className="text-center">{f.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{r.st_idnumber}</td>
            <td className="fs-sm">{r.st_name}</td>
            {fields.map((f) => (
              <td key={f.key} className="text-center"><YesNo v={Number(r[f.key])} /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
